import { Injectable } from "@angular/core";

import ZoomVideo, {
    LocalAudioTrack,
    LocalVideoTrack,
    TestMicrophoneOption,
    TestMicrophoneReturn,
    TestSpeakerOption,
} from "@zoom/videosdk";
import { Subject } from "rxjs";

/**
 * Service use to manage the user settings (Audio and Video) for Zoom live
 */

@Injectable({
    providedIn: "root",
})
export class ZoomLocalSettingsService {
    // Event trigger when the user change the microphone
    public onMicrophoneChanged = new Subject<string>();

    // Event trigger when the user change the speaker
    public onSpeakerChanged = new Subject<string>();

    // Event trigger when the user change the camera
    public onCameraChanged = new Subject<string>();

    // Event when the device list change
    public onDeviceChanged = new Subject<any>();

    /** Current ID microphone device */
    public microphoneDeviceId: string = "default";

    /** Current ID speaker device */
    public speakerDeviceId: string = "default";

    /** Current ID camera device */
    public cameraDeviceId: string;

    private localAudio: LocalAudioTrack;

    private localVideo: LocalVideoTrack;

    constructor() {
        this.localAudio = ZoomVideo.createLocalAudioTrack();
        navigator.mediaDevices.addEventListener("devicechange", (event) => {
            this.onDeviceChanged.next(event);
        });
    }

    /**
     * Start a preview for camera
     * @param videoElement <video> in which the preview is shown
     * @param deviceId Camera device ID
     */
    public async startCameraPreview(
        videoElement: HTMLVideoElement,
        deviceId?: string,
    ): Promise<void> {
        if (!videoElement) return;
        await this.stopCameraPreview();
        this.localVideo = ZoomVideo.createLocalVideoTrack(deviceId);
        this.localVideo.start(videoElement);
    }

    /**
     * Stop the camera preview
     */
    public async stopCameraPreview() {
        if (this.localVideo) {
            await this.localVideo.stop().catch((e) => {});
        }
    }

    /**
     * Change the camera device
     * @param deviceId
     */
    public setCameraDevice(deviceId: string): void {
        this.cameraDeviceId = deviceId;
        this.onCameraChanged.next(deviceId);
    }

    /**
     * Change the microphone device
     * @param deviceId
     */
    public setMicrophoneDevice(deviceId: string): void {
        this.microphoneDeviceId = deviceId;
        this.onMicrophoneChanged.next(deviceId);
    }

    /**
     * Change the speaker device
     * @param deviceId
     */
    public setSpeakerDevice(deviceId: string): void {
        this.speakerDeviceId = deviceId;
        this.onSpeakerChanged.next(deviceId);
    }

    /**
     * Get all devices by kind
     * @param kind audioinput, audiooutput, ...
     * @returns Promise of an array of devices
     */
    public static async getDevicesByKind(kind): Promise<MediaDeviceInfo[]> {
        const devices = await this.getDevices();
        return devices.filter((device) => device.kind === kind);
    }

    /**
     * Get all devices
     * @returns Promise of an array of devices
     */
    public static getDevices(): Promise<MediaDeviceInfo[]> {
        return new Promise<MediaDeviceInfo[]>((resolve, reject) => {
            navigator.mediaDevices.getUserMedia({ audio: true }).then((r) => {
                navigator.mediaDevices.enumerateDevices().then((medias) => {
                    resolve(medias);
                });
            });
        });
    }

    /**
     * Test the microphone
     * @param options : TestMicrophoneOption
     * @see https://marketplacefront.zoom.us/sdk/custom/web/interfaces/localaudiotrack.html#testmicrophone
     * @returns TestMicrophoneReturn
     */
    public testMicrophone(
        options?: TestMicrophoneOption,
    ): TestMicrophoneReturn {
        return this.localAudio.testMicrophone(options);
    }

    /**
     * Play a sound to test the current speaker
     * @param options : TestSpeakerOption
     * @returns Promise<void> : resolve when the test end
     */
    public testSpeaker(options?: TestSpeakerOption): Promise<void> {
        const tester = this.localAudio.testSpeaker(options);

        return new Promise((resolve) => {
            setTimeout(() => {
                // Wait 5s for the sound being played
                tester.destroy(); // Destroy tester
                resolve(); // End the test
            }, 5000);
        });
    }
}
